/* RadarLayer — ORIO Radar map overlays.
   Consumes a RadarLayerConfig produced by radarPrefsToLayerConfig():
     • fires    — EFFIS Copernicus WMS (public, no key)
     • weather  — Protezione Civile IT criticality zones (GeoJSON polygons)
     • disease  — Endemic canine disease zones (static polygons for
                  leishmaniosi, filaria, zecche); diseaseFilter picks which.
     • heat     — Real-time heat warning circles per Italian city
                  (Open-Meteo apparent_temperature ≥ 27°C).
*/
import React, { useEffect, useState } from "react";
import { WMSTileLayer, GeoJSON, Polygon, Circle, Popup } from "react-leaflet";
import { api } from "@/lib/api";

const LEVEL_STYLES = {
  green:  { color: "#34D399", fillColor: "#34D399", fillOpacity: 0.10, weight: 1 },
  yellow: { color: "#EABE2F", fillColor: "#EABE2F", fillOpacity: 0.25, weight: 1.5 },
  orange: { color: "#F4813C", fillColor: "#F4813C", fillOpacity: 0.35, weight: 2 },
  red:    { color: "#E94545", fillColor: "#E94545", fillOpacity: 0.45, weight: 2 },
};

const DISEASE_STYLES = {
  leishmaniosi: { fillColor: "#B24BF3", color: "#8324C9" },
  filaria:      { fillColor: "#4B8BF3", color: "#2464C9" },
  zecche:       { fillColor: "#F4B83C", color: "#C28510" },
};

const SEVERITY_OPACITY = { medium: 0.14, high: 0.22, critical: 0.32 };

// Heat circle opacity by severity — higher = more red.
const HEAT_FILL_OPACITY = { low: 0.15, medium: 0.22, high: 0.32, critical: 0.42 };

export default function RadarLayer({ enabled }) {
  const [fires, setFires] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [diseases, setDiseases] = useState(null);
  const [heat, setHeat] = useState(null);

  useEffect(() => {
    if (enabled.fires && !fires) {
      api.get("/radar/fires").then((r) => setFires(r.data)).catch(() => {});
    }
    if (enabled.weather && !alerts) {
      api.get("/radar/weather-alerts").then((r) => setAlerts(r.data)).catch(() => {});
    }
    if (enabled.disease && !diseases) {
      api.get("/radar/disease-zones").then((r) => setDiseases(r.data)).catch(() => {});
    }
    if (enabled.heat && !heat) {
      api.get("/radar/heat").then((r) => setHeat(r.data)).catch(() => {});
    }
  }, [enabled.fires, enabled.weather, enabled.heat, enabled.disease,
      fires, alerts, diseases, heat]);

  const diseaseFilter = enabled.diseaseFilter || {};

  return (
    <>
      {enabled.fires && fires?.wms && (
        <WMSTileLayer
          url={fires.wms.url}
          layers={fires.wms.layers}
          format={fires.wms.format}
          transparent={fires.wms.transparent}
          version={fires.wms.version}
          opacity={0.55}
          attribution={fires.wms.attribution}
        />
      )}

      {/* Real fire hotspots (NASA FIRMS MODIS) — dramatic pulsing circles */}
      {enabled.fires && fires?.hotspots && fires.hotspots.map((h, i) => {
        const isHigh = h.confidence >= 70;
        return (
          <React.Fragment key={`fire-${i}-${h.lat}-${h.lng}`}>
            {/* Outer glow */}
            <Circle
              center={[h.lat, h.lng]}
              radius={isHigh ? 4000 : 2500}
              pathOptions={{
                color: "transparent",
                fillColor: isHigh ? "#E94545" : "#F4813C",
                fillOpacity: 0.14,
                weight: 0,
                interactive: false,
              }}
            />
            {/* Core */}
            <Circle
              center={[h.lat, h.lng]}
              radius={isHigh ? 1500 : 900}
              pathOptions={{
                color: isHigh ? "#B71C1C" : "#E94545",
                fillColor: isHigh ? "#E94545" : "#F4813C",
                fillOpacity: 0.7,
                weight: 2,
              }}
            >
              <Popup>
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700, color: isHigh ? "#B71C1C" : "#C25612" }}>
                    🔥 Incendio rilevato
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginTop: 2 }}>
                    Confidenza {h.confidence}%
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: "#4A4A4A", lineHeight: 1.4 }}>
                    Rilevato dai satelliti MODIS (NASA FIRMS). Evita l&apos;area e le passeggiate sotto vento se sei nelle vicinanze.
                  </div>
                  {h.acquired_at && (
                    <div style={{ marginTop: 8, fontSize: 10, color: "#9A9A9A" }}>
                      Acquisito {new Date(h.acquired_at).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                </div>
              </Popup>
            </Circle>
          </React.Fragment>
        );
      })}

      {enabled.weather && alerts?.zones && alerts.zones
        .filter((z) => z.level !== "green" && z.geometry)
        .map((z) => (
          <GeoJSON
            key={`pc-${z.zone_id}`}
            data={z.geometry}
            style={LEVEL_STYLES[z.level] || LEVEL_STYLES.yellow}
          >
            <Popup>
              <div style={{ minWidth: 180 }}>
                <div style={{ fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", color: "#9A9A9A" }}>
                  Allerta {z.level === "yellow" ? "gialla" : z.level === "orange" ? "arancione" : "rossa"}
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, marginTop: 2 }}>{z.zone_name}</div>
                {z.region && <div style={{ fontSize: 11, color: "#7A7A7A" }}>{z.region}</div>}
                <div style={{ marginTop: 8, fontSize: 11, color: "#4A4A4A" }}>
                  {z.risks?.temporali !== "green" && <div>⛈️ Temporali: <b>{z.risks.temporali}</b></div>}
                  {z.risks?.idrogeologico !== "green" && <div>🏞️ Idrogeologico: <b>{z.risks.idrogeologico}</b></div>}
                  {z.risks?.idraulico !== "green" && <div>🌊 Idraulico: <b>{z.risks.idraulico}</b></div>}
                </div>
                <div style={{ marginTop: 8, fontSize: 10, color: "#9A9A9A" }}>
                  Dati Protezione Civile
                </div>
              </div>
            </Popup>
          </GeoJSON>
        ))}

      {/* HEAT — real-time temperature circles per major Italian city */}
      {enabled.heat && heat?.zones && heat.zones.map((z) => (
        <Circle
          key={z.id}
          center={[z.lat, z.lng]}
          radius={z.radius_km * 1000}
          pathOptions={{
            color: z.color,
            fillColor: z.color,
            fillOpacity: HEAT_FILL_OPACITY[z.level] || 0.22,
            weight: 1.2,
            opacity: 0.7,
            dashArray: z.level === "critical" ? undefined : "6 4",
          }}
        >
          <Popup>
            <div style={{ minWidth: 200 }}>
              <div style={{ fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", color: z.color, fontWeight: 700 }}>
                {z.level === "critical" ? "Rischio estremo" :
                 z.level === "high"     ? "Rischio elevato" :
                 z.level === "medium"   ? "Attenzione caldo" : "Caldo iniziale"}
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>
                {z.city} · <span style={{ color: z.color }}>{z.feels_c}°C</span>
              </div>
              <div style={{ fontSize: 11, color: "#7A7A7A", marginTop: 2 }}>
                Temperatura percepita · aria {z.temp_c}°C
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: "#4A4A4A", lineHeight: 1.4 }}>
                {z.advice}
              </div>
              <div style={{ marginTop: 8, fontSize: 10, color: "#9A9A9A" }}>
                Dati Open-Meteo · aggiornato ogni 15 minuti
              </div>
            </div>
          </Popup>
        </Circle>
      ))}

      {enabled.disease && diseases?.zones && diseases.zones
        .filter((z) => diseaseFilter[z.disease] !== false)
        .filter((z) => diseaseFilter[z.disease] === true || Object.keys(diseaseFilter).length === 0)
        .map((z) => {
          const style = DISEASE_STYLES[z.disease] || DISEASE_STYLES.zecche;
          const fillOpacity = SEVERITY_OPACITY[z.severity] || 0.20;
          return (
            <Polygon
              key={z.id}
              positions={z.polygon}
              pathOptions={{
                color: style.color, fillColor: style.fillColor,
                fillOpacity, weight: 1.2, opacity: 0.6, dashArray: "6 3",
              }}
            >
              <Popup>
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", color: style.color }}>
                    Zona endemica · {z.severity === "critical" ? "critica" : z.severity === "high" ? "alta" : "media"}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginTop: 2 }}>{z.label}</div>
                  <div style={{ marginTop: 8, fontSize: 12, color: "#4A4A4A", lineHeight: 1.4 }}>
                    {z.advice}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 10, color: "#9A9A9A" }}>
                    Stagione: {z.months_active?.map((m) => ["gen","feb","mar","apr","mag","giu","lug","ago","set","ott","nov","dic"][m-1]).join(" · ")}
                  </div>
                </div>
              </Popup>
            </Polygon>
          );
        })}
    </>
  );
}
